/**
 * Pure view-model helpers for the salary-history card
 * (`salary_history_card.tsx`): which display status each
 * `employee_compensation` row has, which rows can still be cancelled, and
 * since when the current pay has been in effect. No React.
 *
 * The backend stores three lifecycle statuses (`active` / `cancelled` /
 * `superseded`); "scheduled", "current" and "past" are derived here from an
 * active row's `effective_from` relative to today, because they change with
 * the calendar, not with any write. The mapping onto the shared
 * `<StatusPill>` lives in this ONE module so the same concept can never be
 * coloured two different ways (docs/design-system/CLAUDE.md).
 */
import type { StatusPillStatus } from '@jumpstone/ui-react';
import type { EmployeeCompensationRecord } from '../../../data/use_employee_compensation';

export type CompensationDisplayStatus =
  | 'scheduled'
  | 'current'
  | 'past'
  | 'cancelled'
  | 'superseded';

/** Display status -> shared `<StatusPill>` member. `scheduled` is a first-class pill member (a recorded-but-not-yet-effective change). */
export const COMPENSATION_STATUS_PILL: Record<
  CompensationDisplayStatus,
  StatusPillStatus
> = {
  scheduled: 'scheduled',
  current: 'active',
  past: 'inactive',
  cancelled: 'voided',
  superseded: 'inactive',
};

/** Display status -> translation key (wp namespace). */
export const COMPENSATION_STATUS_KEY: Record<
  CompensationDisplayStatus,
  string
> = {
  scheduled: 'employees.compensation_status.scheduled',
  current: 'employees.compensation_status.current',
  past: 'employees.compensation_status.past',
  cancelled: 'employees.compensation_status.cancelled',
  superseded: 'employees.compensation_status.superseded',
};

export interface SalaryHistoryViewRow {
  row: EmployeeCompensationRecord;
  status: CompensationDisplayStatus;
  /** Only a future-dated, non-baseline entry is offered for cancellation; the server still rejects one an approved pay run consumed. */
  can_cancel: boolean;
}

/** Newest effective date first; ties broken by newest creation. */
function compareNewestFirst(
  a: EmployeeCompensationRecord,
  b: EmployeeCompensationRecord,
): number {
  if (a.effective_from !== b.effective_from) {
    return a.effective_from < b.effective_from ? 1 : -1;
  }
  if (a.created_at === b.created_at) return 0;
  return a.created_at < b.created_at ? 1 : -1;
}

/** The active row in force today: the newest active row with `effective_from <= today`. */
export function findCurrentRow(
  rows: readonly EmployeeCompensationRecord[],
  today: string,
): EmployeeCompensationRecord | null {
  const inForce = rows
    .filter((r) => r.status === 'active' && r.effective_from <= today)
    .sort(compareNewestFirst);
  return inForce.at(0) ?? null;
}

/** ISO date the current pay took effect, or `null` when no history row is in force yet. */
export function currentEffectiveSince(
  rows: readonly EmployeeCompensationRecord[],
  today: string,
): string | null {
  return findCurrentRow(rows, today)?.effective_from ?? null;
}

export function buildSalaryHistoryView(
  rows: readonly EmployeeCompensationRecord[],
  today: string,
): SalaryHistoryViewRow[] {
  const currentId = findCurrentRow(rows, today)?.id ?? null;
  return [...rows].sort(compareNewestFirst).map((row) => {
    let status: CompensationDisplayStatus;
    if (row.status === 'cancelled') status = 'cancelled';
    else if (row.status === 'superseded') status = 'superseded';
    else if (row.effective_from > today) status = 'scheduled';
    else if (row.id === currentId) status = 'current';
    else status = 'past';
    return {
      row,
      status,
      can_cancel: status === 'scheduled' && row.source !== 'baseline',
    };
  });
}

/** The amount a row pays: the monthly salary (salaried) or the hourly rate (hourly), in minor units. */
export function rowAmountCents(row: EmployeeCompensationRecord): number {
  return row.pay_basis === 'salaried'
    ? (row.monthly_salary_cents ?? 0)
    : row.hourly_rate_cents;
}
